import * as Dialog from '@radix-ui/react-dialog';
import React, { useState } from 'react';

import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Flex } from '@/components/shared/layout/flex';
import { formatPrice } from '@/components/shared/utility/format';
import { CvModel } from '@/services/contentstack/models/cv-model';
import { keyframes, styled } from '@/stitches.config';
import { ITest } from '@/types/tracking';

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

const contentShow = keyframes({
  '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(.96)' },
  '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
});

const DialogOverlay = styled(Dialog.Overlay, {
  position: 'fixed',
  width: 'calc(100% + 1px)',
  height: 'calc(var(--viewport-height) + 1px)',
  top: -1,
  left: -1,
  bc: '$backdrop70',
  zIndex: '1001',
  cursor: 'pointer',
  '@media (prefers-reduced-motion: no-preference)': {
    animationName: `${overlayShow}`,
    animationDuration: '1s',
    animationTimingFunction: 'var(--transition-easing)',
  },
});

const DialogContent = styled(Dialog.Content, {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 32px)',
  maxWidth: 280,
  zIndex: '1002',
  br: '$rounded-1-5',
  bc: '$white',
  border: '1px solid $gray300',
  bs: '$shadow200',
  '@media (prefers-reduced-motion: no-preference)': {
    animationName: `${contentShow}`,
    animationDuration: 'var(--transition-duration)',
    animationTimingFunction: 'var(--transition-easing)',
  },
  '&:focus': { outline: 'none' },
  '@sm': {
    width: 'calc(100% - 96px)',
  },
});

const ButtonInfo = styled('button', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  outline: 'none',
  border: 'none',
  bc: '$transparent',
  cursor: 'pointer',
});

const PriceWrap = styled('div', {
  paddingBlock: '$space-6',
  paddingInline: '$space-4',
});

interface IPriceDialogProps {
  models: CvModel[];
  test?: ITest;
}

const PriceDialog: React.FC<IPriceDialogProps> = ({
  models = [],
  test,
}: IPriceDialogProps) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  return (
    <>
      <Dialog.Root
        open={openDialog}
        onOpenChange={(open) => setOpenDialog(open)}
      >
        <Dialog.Trigger asChild>
          <ButtonInfo
            aria-label="button"
            data-test={test?.dataTest || 'price_toolTips'}
          >
            <Icon icon="info" size="xs" color="icon-blue" />
          </ButtonInfo>
        </Dialog.Trigger>
        <Dialog.Portal>
          <DialogOverlay data-test="price_dialog_overlay" />
          <DialogContent data-test="price_dialog_content">
            <PriceWrap>
              {[...models]
                .sort((a, b) => a?.price - b?.price)
                .map((model: CvModel, index: number) => (
                  <Flex
                    key={index}
                    alignItems="center"
                    justifyContent="center"
                    columnGap="2"
                  >
                    <Text size="2xs" variant="text-gray">
                      รุ่น {model.name}:
                    </Text>
                    <Text font="bold" variant="text-gray-dark">
                      {formatPrice(model.price)}
                    </Text>
                  </Flex>
                ))}
              <ButtonIcon
                onClick={() => setOpenDialog(false)}
                icon="close"
                variant="button-icon-color-gray-dark"
                size="xs"
                isTransparent
                css={{
                  position: 'absolute',
                  right: '$space-1',
                  top: '$space-1',
                }}
              />
            </PriceWrap>
          </DialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

PriceDialog.displayName = 'SectionProductComparePriceDialog';
export default PriceDialog;
